// src/pages/churches/Churches.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ref, onValue, remove } from 'firebase/database';
import { database } from '../../config/firebase';

const Churches = () => {
    const [churches, setChurches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [deletingId, setDeletingId] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [selectedChurch, setSelectedChurch] = useState(null);

    useEffect(() => {
        const churchesRef = ref(database, 'churches');

        const unsubscribe = onValue(churchesRef, (snapshot) => {
            setLoading(true);

            if (snapshot.exists()) {
                const churchesData = [];

                snapshot.forEach((childSnapshot) => {
                    const church = {
                        id: childSnapshot.key,
                        ...childSnapshot.val()
                    };
                    churchesData.push(church);
                });

                // Trier par nom d'église
                churchesData.sort((a, b) => a.nom.localeCompare(b.nom));

                setChurches(churchesData);
            } else {
                setChurches([]);
            }

            setLoading(false);
        }, (error) => {
            console.error("Erreur lors du chargement des églises:", error);
            setError("Impossible de charger la liste des églises. Veuillez réessayer plus tard.");
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleDeleteClick = (church) => {
        setSelectedChurch(church);
        setShowConfirmModal(true);
    };

    const confirmDelete = async () => {
        if (!selectedChurch) return;

        setDeletingId(selectedChurch.id);

        try {
            // Vérifier si l'église a des membres
            const churchMembersRef = ref(database, `churchMembers/${selectedChurch.id}`);
            const churchMembersSnapshot = await new Promise((resolve, reject) => {
                onValue(churchMembersRef, resolve, { onlyOnce: true }, reject);
            });

            if (churchMembersSnapshot.exists()) {
                // Si l'église a des membres, ne pas permettre la suppression
                setError(`Impossible de supprimer "${selectedChurch.nom}" car cette église a des membres associés.`);
                setDeletingId(null);
                setShowConfirmModal(false);
                return;
            }

            // Supprimer l'église
            const churchRef = ref(database, `churches/${selectedChurch.id}`);
            await remove(churchRef);

            // Supprimer les statistiques de l'église
            const churchStatsRef = ref(database, `statistics/churches/${selectedChurch.id}`);
            await remove(churchStatsRef);

            setDeletingId(null);
            setShowConfirmModal(false);
            setSelectedChurch(null);
        } catch (error) {
            console.error("Erreur lors de la suppression de l'église:", error);
            setError(`Erreur lors de la suppression: ${error.message}`);
            setDeletingId(null);
        }
    };

    const cancelDelete = () => {
        setShowConfirmModal(false);
        setSelectedChurch(null);
    };

    // Filtrer les églises en fonction du terme de recherche
    const filteredChurches = churches.filter(church =>
        church.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (church.localisation && church.localisation.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <div className="px-4 sm:px-6 lg:px-8 py-8">
            <div className="sm:flex sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Gestion des églises</h1>
                    <p className="mt-2 text-sm text-gray-700">
                        Liste des églises enregistrées dans le système.
                    </p>
                </div>
                <Link to="/dashboard"
                      className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-500">
                    <svg className="-ml-1 mr-1 h-5 w-5 text-indigo-500" xmlns="http://www.w3.org/2000/svg"
                         viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd"
                              d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                              clipRule="evenodd"/>
                    </svg>
                    Tableau de bord
                </Link>
                <div className="mt-4 sm:mt-0">
                    <Link
                        to="/churches/add"
                        className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                        </svg>
                        Ajouter une église
                    </Link>
                </div>
            </div>

            {error && (
                <div className="mt-4 bg-red-50 border-l-4 border-red-500 text-red-700 p-4" role="alert">
                    <p>{error}</p>
                </div>
            )}

            <div className="mt-6">
                <div className="mb-4">
                    <div className="relative rounded-md shadow-sm max-w-md">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <input
                            type="text"
                            className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                            placeholder="Rechercher une église..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
                    </div>
                ) : (
                    <>
                        {filteredChurches.length === 0 ? (
                            <div className="bg-white shadow overflow-hidden rounded-md p-6 text-center">
                                <p className="text-gray-500">
                                    {searchTerm ? 'Aucune église ne correspond à votre recherche.' : 'Aucune église enregistrée. Ajoutez-en une pour commencer.'}
                                </p>
                            </div>
                        ) : (
                            <div className="bg-white shadow overflow-hidden sm:rounded-md">
                                <ul className="divide-y divide-gray-200">
                                    {filteredChurches.map((church) => (
                                        <li key={church.id}>
                                            <div className="px-4 py-4 sm:px-6 flex items-center justify-between">
                                                <div className="flex items-center">
                                                    <div className="min-w-0 flex-1">
                                                        <p className="text-sm font-medium text-indigo-600 truncate">{church.nom}</p>
                                                        {church.localisation && (
                                                            <p className="mt-1 text-sm text-gray-500">
                                <span className="flex items-center">
                                  <svg className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                  </svg>
                                    {church.localisation}
                                </span>
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex space-x-2">
                                                    <Link
                                                        to={`/churches/${church.id}`}
                                                        className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                                    >
                                                        <svg className="mr-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                                            <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                                                        </svg>
                                                        Voir
                                                    </Link>
                                                    <Link
                                                        to={`/churches/${church.id}/edit`}
                                                        className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                                    >
                                                        <svg className="mr-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                                        </svg>
                                                        Modifier
                                                    </Link>
                                                    <button
                                                        onClick={() => handleDeleteClick(church)}
                                                        disabled={deletingId === church.id}
                                                        className={`inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${deletingId === church.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                    >
                                                        {deletingId === church.id ? (
                                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-red-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                        ) : (
                                                            <svg className="mr-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                                            </svg>
                                                        )}
                                                        Supprimer
                                                    </button>
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Modal de confirmation de suppression */}
            {showConfirmModal && selectedChurch && (
                <div className="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                    <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                        <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                    </div>
                                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                        <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                            Supprimer l'église
                                        </h3>
                                        <div className="mt-2">
                                            <p className="text-sm text-gray-500">
                                                Êtes-vous sûr de vouloir supprimer l'église "{selectedChurch.nom}" ? Cette action est irréversible.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                <button
                                    type="button"
                                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={confirmDelete}
                                    disabled={deletingId !== null}
                                >
                                    {deletingId !== null ? 'Suppression...' : 'Supprimer'}
                                </button>
                                <button
                                    type="button"
                                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={cancelDelete}
                                >
                                    Annuler
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Churches;
